/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: BI Dashboard
 *| (C) Copyright IBM Corp. 2020, 2021
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

define([
	'underscore',
	'../../../lib/@waca/upgrades/UpgradeBase',
	'./utils/WidgetUpgradeUtils',
	'./utils/VCF_RuleConstants',
	'./utils/VCFUpgradeUtils',
	'../../../app/util/DeepClone'
], (_, UpgradeBase, WidgetUpgradeUtils, VCF_RuleConstants, VCFUpgradeUtils) => {

	/**
	* Upgrade kpi widgets to the new version based on the KPI widget ConbditionalFormatting
	**/

	const DefaultPaletteLength = 5;
	const KPIVisId = 'kpi';
	const SummaryVisId = 'summary';

	const rangeRuleTemplate = {
		'ruleType': VCF_RuleConstants.RULE_TYPES.RANGE,
		'startCondition': {
			'type': VCF_RuleConstants.RULE_OPERATORS.greaterThanOrEqualTo,
			'value': undefined
		},
		'endCondition': {
			'type': VCF_RuleConstants.RULE_OPERATORS.lessThan,
			'value': undefined
		},
		'ruleStyle': {
			'textColor': undefined,
			'shape': undefined
		},
		'operator': VCF_RuleConstants.RangeOperators.and
	};

	const minThresholdRuleTemplate = {
		'ruleType': VCF_RuleConstants.RULE_TYPES.THRESHOLD,
		'startCondition': {
			'type': VCF_RuleConstants.RULE_OPERATORS.lessThan,
			'value': undefined
		},
		'ruleStyle': {
			'textColor': 'color4',
			'shape': undefined
		},
		'operator': VCF_RuleConstants.RangeOperators.none
	};

	const maxThresholdRuleTemplate = {
		'ruleType': VCF_RuleConstants.RULE_TYPES.THRESHOLD,
		'startCondition': {
			'type': VCF_RuleConstants.RULE_OPERATORS.greaterThanOrEqualTo,
			'value': undefined
		},
		'ruleStyle': {
			'textColor': 'color0',
			'shape': undefined
		},
		'operator': VCF_RuleConstants.RangeOperators.none
	};

	const equalThresholdRuleTemplate = {
		'ruleType': VCF_RuleConstants.RULE_TYPES.THRESHOLD,
		'startCondition': {
			'type': VCF_RuleConstants.RULE_OPERATORS.equalTo,
			'value': undefined
		},
		'ruleStyle': {
			'textColor': 'color2',
			'shape': undefined
		},
		'operator': VCF_RuleConstants.RangeOperators.none
	};
	class KPI_VCF_Upgrade extends UpgradeBase {

		constructor() {
			super();
			this.VERSION = 1805;
		}

		/**
		 * Perform upgrade
		 *
		 * @param {object} spec - spec to perform upgrade on
		 *
		 * @return {Promise} Promise to be resolved when upgrade performed
		 */
		up(spec) {
			if (!WidgetUpgradeUtils.specHasWidgets(spec)) {
				return Promise.resolve(spec);
			}

			// Upgrade all the KPI widgets
			const promisesOfUpgrading = [];
			Object.values(spec.widgets).forEach((widgetModel) => {
				if (widgetModel && widgetModel.visId) {
					const widgetVisId = widgetModel.visId;
					const widgetUISpecs = this._getObjects(spec.layout, 'id', widgetModel.id);
					const widgetUISpec = widgetUISpecs.length > 0 ? widgetUISpecs[0] : undefined;
					if (widgetVisId === KPIVisId && widgetUISpec){
						let legacyCFSpec = (widgetUISpec.content && widgetUISpec.content.conditionalFormatting && (widgetUISpec.content.conditionalFormatting.enabled !== undefined)) ? widgetUISpec.content.conditionalFormatting: undefined;
						const promise = this._getTargetConditionalFormat(widgetModel, legacyCFSpec).then((newCFSpec) => {
							if (newCFSpec) {
								if (legacyCFSpec && legacyCFSpec.conditionalFormats) {
									legacyCFSpec.conditionalFormats.push(newCFSpec);
								} else {
									widgetUISpec.content = {
										conditionalFormatting: {
											conditionalFormats: [newCFSpec]
										}
									};
								}
							}
						});
						promisesOfUpgrading.push(promise);
					} else if (widgetVisId === SummaryVisId && widgetUISpec){
						if (widgetUISpec.content.conditionalFormatting){
							widgetUISpec.content.conditionalFormatting = undefined;
						}
					}
				}
			});
			return Promise.all(promisesOfUpgrading).then(() => spec);
		}

		// Downgrade is not available in CA
		down(spec) {
			return Promise.resolve(spec);
		}

		_getObjects(obj, key, val) {
			let objects = [];
			for (const i in obj) {
				if (!obj.hasOwnProperty(i)) continue;
				if (typeof obj[i] == 'object') {
					objects = objects.concat(this._getObjects(obj[i], key, val));
				} else
				if (i == key && obj[i] == val) {
					objects.push(obj);
				}
			}
			return objects;
		}

		_getSlotName(slots, mappingId) {
			let slotName = '';
			const foundSlot =_.find(slots, (slot) => {
				return slot.dataItems[0] === mappingId;
			});
			if (foundSlot) {
				slotName = foundSlot.name;
			}
			return slotName;
		}

		_getTargetConditionalFormat(widgetModel, legacyCFSpec) {
			let resultPromise = Promise.resolve();
			if (legacyCFSpec) {

				// Default conditionalFormat
				const newCFSpec = {
					'dataItemId': undefined,
					'paletteId': undefined,
					'backgroundOpacity': 0.5,
					'scale': undefined,
					'rules': []
				};



				const dataItems = [];
				if(widgetModel.data &&  widgetModel.data.dataViews && widgetModel.data.dataViews.length > 0){
					widgetModel.data.dataViews.forEach((dataView) => {
						dataItems.push(...dataView.dataItems);
					});
				}
				dataItems.forEach((dataItem) => {
					const slotName = this._getSlotName(widgetModel.slotmapping.slots, dataItem.id);
					if ( slotName === 'actual') {
						newCFSpec.dataItemId = dataItem.itemId;
					} else if ( slotName === 'goal') {
						newCFSpec.colorBy  = dataItem.itemId;
					}
				});

				newCFSpec.paletteId = legacyCFSpec.paletteId;

				newCFSpec.scale = legacyCFSpec.rangeScale;

				if (legacyCFSpec.enabled) {
					const paletteReversed = !legacyCFSpec.paletteReversed; // the value of `paletteReversed` in the legacy is just the opposite of the actual UI of the product. So, flip it to get the correct value.
					const ranges = legacyCFSpec.ranges;
					const rangesLength = ranges ? ranges.length : 0;
					let rule = null;
					const paletteLengthPromise = newCFSpec.paletteId.startsWith('condColorPalette') ? Promise.resolve(DefaultPaletteLength) : this.data.dashboardApi.getFeature('Palette').getPalette(newCFSpec.paletteId).then((paletteInfo) => {
						// we'll have palette info when this palette is a custom palette and existing on this server
						return paletteInfo.content.fills.length;
					}, () => {
						return DefaultPaletteLength;
					});
					resultPromise = paletteLengthPromise.then((paletteLength) => {
						const colorIndexMap = VCFUpgradeUtils.buildIndexMap(paletteLength, rangesLength);
						if(paletteReversed){
							colorIndexMap.reverse();
						}
						const defaultFillColors = _.range(paletteLength).map((index) => {
							return 'color' + index;
						});
						for(let i = 0; i < rangesLength; i++) {
							const legacyRange = ranges[i];
							if (legacyRange.startValue === null) {
								rule = _.deepClone(minThresholdRuleTemplate);
								rule.startCondition.value = legacyRange.endValue;
							} else if (legacyRange.endValue === null) {
								rule = _.deepClone(maxThresholdRuleTemplate);
								rule.startCondition.value = legacyRange.startValue;
							} else if (legacyRange.startValue === legacyRange.endValue) {
								rule = _.deepClone(equalThresholdRuleTemplate);
								rule.startCondition.value = legacyRange.startValue;
							} else {
								rule = _.deepClone(rangeRuleTemplate);
								rule.startCondition.value = legacyRange.startValue;
								rule.endCondition.value = legacyRange.endValue;
							}

							if (legacyRange.shape) {
								rule.ruleStyle.shape = legacyRange.shape;
							}

							if (legacyRange.userSetColorId) {
								rule.ruleStyle.textColor = legacyRange.userSetColorId;
							} else if (legacyRange.defaultColorId) {
								rule.ruleStyle.textColor = legacyRange.defaultColorId;
							} else {
								rule.ruleStyle.textColor = defaultFillColors[colorIndexMap[i]];
							}
							const systemColorRegex = /^color[0-9]+/;
							if (systemColorRegex.test(rule.ruleStyle.textColor) && !defaultFillColors.includes(rule.ruleStyle.textColor)){
								rule.ruleStyle.textColor = '';
							}

							newCFSpec.rules.push(rule);
						}
						return newCFSpec;
					});
				} else {
					return Promise.resolve(newCFSpec);
				}

			}
			return resultPromise;
		}
	}

	return new KPI_VCF_Upgrade();
});
